/**
 * Memory monitoring service for MobyNet file transfers
 * Provides real-time memory usage metrics and cache quota warnings
 */

export interface MemoryUsage {
  usedMB: number;
  totalMB: number;
  limitMB: number;
  usagePercentage: number;
  isHighUsage: boolean;
  isCriticalUsage: boolean;
  // OPFS storage quota tracking
  opfsUsedMB?: number;
  opfsQuotaMB?: number;
  opfsUsagePercentage?: number;
  opfsIsHighUsage?: boolean;
  opfsIsCriticalUsage?: boolean;
}

export interface FileMemoryEstimate {
  fileSizeMB: number;
  estimatedMemoryMB: number;
  memoryRatio: number;
  wouldExceedLimit: boolean;
  warningLevel: 'safe' | 'warning' | 'critical';
}

class MemoryMonitor {
  private static instance: MemoryMonitor;
  private callbacks: Set<(usage: MemoryUsage) => void> = new Set();
  private pollingInterval: number | null = null;
  
  // CONNECTION-AWARE POLLING: Pause during peer connection setup
  private connectionCheckCallback: (() => boolean) | null = null;
  
  // FIREFOX CIRCUIT BREAKER: Prevent repeated OPFS estimate failures from blocking
  private opfsCircuitBreakerOpen = false;
  private opfsFailureCount = 0;
  private readonly OPFS_FAILURE_THRESHOLD = 3;
  private readonly OPFS_CIRCUIT_RESET_MS = 60000; // 1 minute
  
  // CRITICAL: Cache for navigator.storage.estimate() to prevent thread blocking
  // This API call can block the main thread for 50ms+, causing setTimeout violations
  private opfsCache: {
    data: {
      usedMB: number;
      quotaMB: number;
      usagePercentage: number;
      isHighUsage: boolean;
      isCriticalUsage: boolean;
    } | null;
    timestamp: number;
  } = { data: null, timestamp: 0 };
  private readonly OPFS_CACHE_DURATION_MS = 5000; // 5 second cache
  // Threshold-event-driven logging — only log on state transition
  private lastOpfsThresholdState: 'normal' | 'high' | 'critical' = 'normal';

  static getInstance(): MemoryMonitor {
    if (!MemoryMonitor.instance) {
      MemoryMonitor.instance = new MemoryMonitor();
    }
    return MemoryMonitor.instance;
  }

  /**
   * Get OPFS storage quota usage (with caching to prevent main thread blocking)
   * CRITICAL: navigator.storage.estimate() can block for 50ms+ causing setTimeout violations
   */
  private async getOPFSStorageUsage(): Promise<{
    usedMB: number;
    quotaMB: number;
    usagePercentage: number;
    isHighUsage: boolean;
    isCriticalUsage: boolean;
  } | null> {
    try {
      // FIREFOX CIRCUIT BREAKER: Skip OPFS estimate if circuit is open (repeated failures)
      if (this.opfsCircuitBreakerOpen) {
        console.debug('[OPFS_CIRCUIT_BREAKER] Circuit open, skipping OPFS estimate');
        return this.opfsCache.data; // Return last known good value
      }
      
      // CRITICAL: Return cached value if still fresh (prevents main thread blocking)
      const now = Date.now();
      if (this.opfsCache.data && (now - this.opfsCache.timestamp) < this.OPFS_CACHE_DURATION_MS) {
        return this.opfsCache.data;
      }
      
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const usedBytes = estimate.usage || 0;
        const reportedQuotaBytes = estimate.quota || 0;
        
        const usedMB = usedBytes / 1024 / 1024;
        const reportedQuotaMB = reportedQuotaBytes / 1024 / 1024;
        
        // CRITICAL FIX: navigator.storage.estimate() often reports unrealistic quotas
        // Cap at reasonable limits based on actual disk space constraints
        const realisticQuotaMB = Math.min(
          reportedQuotaMB,
          10 * 1024, // 10GB max for browser storage (realistic limit)
          usedMB * 20 // At least 20x current usage for growth room
        );
        
        const usagePercentage = realisticQuotaMB > 0 ? (usedMB / realisticQuotaMB) * 100 : 0;
        
        // OPFS-specific thresholds based on observed 480MB transfer failure
        const opfsHighUsageThreshold = 70; // % - OPFS becomes unstable at high usage
        const opfsCriticalUsageThreshold = 85; // % - OPFS failures likely
        
        const result = {
          usedMB,
          quotaMB: realisticQuotaMB,
          usagePercentage,
          isHighUsage: usagePercentage > opfsHighUsageThreshold,
          isCriticalUsage: usagePercentage > opfsCriticalUsageThreshold
        };
        
        // Cache the result with timestamp
        this.opfsCache = { data: result, timestamp: now };
        
        // Reset circuit breaker on success
        if (this.opfsFailureCount > 0) {
          console.debug('[OPFS_CIRCUIT_BREAKER] Resetting failure count after successful estimate');
          this.opfsFailureCount = 0;
        }
        
        // Threshold-event-driven: only log on state transition
        const newState = result.isCriticalUsage ? 'critical' : result.isHighUsage ? 'high' : 'normal';
        if (newState !== this.lastOpfsThresholdState) {
          const direction = newState === 'normal' ? 'recovered' : `entered ${newState}`;
          console.debug(`[OPFS_STORAGE] Storage ${direction}`, {
            usedMB: usedMB.toFixed(1),
            realisticQuotaMB: realisticQuotaMB.toFixed(1),
            usagePercentage: usagePercentage.toFixed(1) + '%',
            previous: this.lastOpfsThresholdState,
          });
          this.lastOpfsThresholdState = newState;
        }
        
        return result;
      }
    } catch (error) {
      console.warn('[OPFS_STORAGE] Failed to get storage estimate:', error);
      
      // FIREFOX CIRCUIT BREAKER: Track failures and open circuit if threshold exceeded
      this.opfsFailureCount++;
      
      if (this.opfsFailureCount >= this.OPFS_FAILURE_THRESHOLD && !this.opfsCircuitBreakerOpen) {
        console.warn('[OPFS_CIRCUIT_BREAKER] Opening circuit after repeated failures', {
          failureCount: this.opfsFailureCount,
          threshold: this.OPFS_FAILURE_THRESHOLD
        });
        this.opfsCircuitBreakerOpen = true;
        
        // Auto-reset circuit breaker after cooldown period
        setTimeout(() => {
          console.log('[OPFS_CIRCUIT_BREAKER] Resetting circuit breaker after cooldown');
          this.opfsCircuitBreakerOpen = false;
          this.opfsFailureCount = 0;
        }, this.OPFS_CIRCUIT_RESET_MS);
      }
    }
    return this.opfsCache.data; // Return last known good value on error
  }

  /**
   * Get current memory usage snapshot (Chromium-focused) with OPFS storage tracking
   */
  async getCurrentUsage(): Promise<MemoryUsage | null> {
    // Get OPFS storage usage first
    const opfsUsage = await this.getOPFSStorageUsage();
    
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      const usedMB = memInfo.usedJSHeapSize / 1024 / 1024;
      const totalMB = memInfo.totalJSHeapSize / 1024 / 1024;
      const limitMB = memInfo.jsHeapSizeLimit / 1024 / 1024;
      const usagePercentage = (usedMB / limitMB) * 100;

      // Chromium-optimized thresholds for large file transfers
      // Based on observed behavior: 480MB file stalls at ~1.5GB actual usage
      const highUsageThreshold = 800; // MB - Conservative for large file transfers
      const criticalUsageThreshold = 75; // % - Account for OPFS/IndexedDB overhead not measured here

      return {
        usedMB,
        totalMB,
        limitMB,
        usagePercentage,
        isHighUsage: usedMB > highUsageThreshold,
        isCriticalUsage: usagePercentage > criticalUsageThreshold,
        // Include OPFS storage data
        opfsUsedMB: opfsUsage?.usedMB,
        opfsQuotaMB: opfsUsage?.quotaMB,
        opfsUsagePercentage: opfsUsage?.usagePercentage,
        opfsIsHighUsage: opfsUsage?.isHighUsage,
        opfsIsCriticalUsage: opfsUsage?.isCriticalUsage
      };
    }

    // Fallback for Firefox and development environments
    // Only log once per session to reduce console spam
    if (!this.opfsCache.data) {
      console.warn('[MEMORY_MONITOR] performance.memory not available, using fallback estimates');
    }
    
    // Conservative fallback based on typical Chromium limits
    const estimatedLimitMB = 2048; // 2GB typical Chromium limit
    const estimatedUsedMB = 400; // Conservative baseline usage
    const estimatedTotalMB = 800; // Conservative total allocation
    const usagePercentage = (estimatedUsedMB / estimatedLimitMB) * 100;
    
    return {
      usedMB: estimatedUsedMB,
      totalMB: estimatedTotalMB,
      limitMB: estimatedLimitMB,
      usagePercentage,
      isHighUsage: estimatedUsedMB > 800,
      isCriticalUsage: usagePercentage > 75,
      // Include OPFS storage data even in fallback
      opfsUsedMB: opfsUsage?.usedMB,
      opfsQuotaMB: opfsUsage?.quotaMB,
      opfsUsagePercentage: opfsUsage?.usagePercentage,
      opfsIsHighUsage: opfsUsage?.isHighUsage,
      opfsIsCriticalUsage: opfsUsage?.isCriticalUsage
    } as MemoryUsage;
  }

  /**
   * Estimate memory usage for a file transfer
   * Based on real-world observations from 480MB file stall analysis
   * Accounts for the complete overhead of the robust transfer system
   */
  async estimateFileMemoryUsage(fileSizeBytes: number): Promise<FileMemoryEstimate> {
    const fileSizeMB = fileSizeBytes / 1024 / 1024;
    
    // REALISTIC memory ratios based on stress test analysis:
    // 480MB file stalled at 23% (~110MB processed) but used 1.5GB+ actual memory
    // This accounts for:
    // - RaptorQ encoding/decoding buffers (2x file size)
    // - Chunk buffering with session management (1MB chunks × burst count)
    // - OPFS staging overhead (not measured by browser memory API)
    // - IndexedDB operations and metadata
    // - Browser heap fragmentation for large allocations
    let memoryRatio: number;

    if (fileSizeMB < 10) {
      // Small files: High fixed overhead from session management
      memoryRatio = 5.0;
    } else if (fileSizeMB < 50) {
      // Medium files: Moderate overhead
      memoryRatio = 4.0;
    } else if (fileSizeMB < 200) {
      // Large files: Realistic overhead based on observed behavior
      memoryRatio = 3.5;
    } else {
      // Very large files: Maximum overhead due to OPFS limitations
      // 480MB file → 1.5GB+ actual usage = 3.1x minimum observed ratio
      memoryRatio = 4.0; // Conservative for files that will hit browser limits
    }

    const estimatedMemoryMB = fileSizeMB * memoryRatio;
    const currentUsage = await this.getCurrentUsage();
    
    let wouldExceedLimit = false;
    let warningLevel: 'safe' | 'warning' | 'critical' = 'safe';

    if (currentUsage) {
      const projectedUsageMB = currentUsage.usedMB + estimatedMemoryMB;
      const projectedPercentage = (projectedUsageMB / currentUsage.limitMB) * 100;
      
      // CRITICAL: Check OPFS storage quota as well
      const opfsProjectedUsageMB = (currentUsage.opfsUsedMB || 0) + fileSizeMB * 1.2; // 20% OPFS overhead
      const opfsProjectedPercentage = currentUsage.opfsQuotaMB ? (opfsProjectedUsageMB / currentUsage.opfsQuotaMB) * 100 : 0;

      wouldExceedLimit = projectedUsageMB > currentUsage.limitMB;
      
      // Conservative thresholds based on 480MB failure analysis
      // Check both JavaScript heap AND OPFS storage limits
      const heapWouldExceed = projectedPercentage > 85 || projectedUsageMB > currentUsage.limitMB * 0.9;
      const opfsWouldExceed = opfsProjectedPercentage > 85 || (currentUsage.opfsIsCriticalUsage && fileSizeMB > 100);
      
      if (heapWouldExceed || opfsWouldExceed) {
        wouldExceedLimit = true;
        warningLevel = 'critical';
      } else if (projectedPercentage > 70 || projectedUsageMB > currentUsage.limitMB * 0.7 || opfsProjectedPercentage > 70) {
        warningLevel = 'warning';
      }
    }

    return {
      fileSizeMB,
      estimatedMemoryMB,
      memoryRatio,
      wouldExceedLimit,
      warningLevel
    };
  }

  /**
   * Set connection check callback to pause polling during peer connections
   * @param callback Returns true if peers are connecting (should pause polling)
   */
  setConnectionCheck(callback: (() => boolean) | null): void {
    this.connectionCheckCallback = callback;
  }
  
  /**
   * Subscribe to memory usage updates
   */
  subscribe(callback: (usage: MemoryUsage) => void): () => void {
    this.callbacks.add(callback);
    
    // Start polling if this is the first subscriber
    if (this.callbacks.size === 1) {
      this.startPolling();
    }

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
      
      // Stop polling if no more subscribers
      if (this.callbacks.size === 0) {
        this.stopPolling();
      }
    };
  }

  private startPolling(): void {
    if (this.pollingInterval) return;

    // CONNECTION-AWARE: Longer interval to reduce event loop pressure
    // 10 seconds instead of 2 seconds - memory monitoring is not time-critical
    this.pollingInterval = window.setInterval(async () => {
      // CONNECTION-AWARE: Skip polling if peers are connecting
      if (this.connectionCheckCallback && this.connectionCheckCallback()) {
        console.debug('[MEMORY_MONITOR] Skipping poll - peers connecting');
        return;
      }
      
      const usage = await this.getCurrentUsage();
      if (usage) {
        this.callbacks.forEach(callback => callback(usage));
      }
    }, 10000); // Poll every 10 seconds (reduced from 2s)
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Format memory usage for display
   */
  formatMemoryUsage(usage: MemoryUsage): string {
    return `${usage.usedMB.toFixed(1)}MB / ${usage.limitMB.toFixed(1)}MB (${usage.usagePercentage.toFixed(1)}%)`;
  }

  /**
   * Get memory status color for UI
   */
  getMemoryStatusColor(usage: MemoryUsage): string {
    if (usage.isCriticalUsage) return 'text-red-500';
    if (usage.isHighUsage) return 'text-yellow-500';
    return 'text-green-500';
  }
}

export const memoryMonitor = MemoryMonitor.getInstance();
